import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App"; // Assuming AuthGuard is correctly implemented
import { toast } from "react-toastify"; // Import ToastContainer and toast
import "react-toastify/dist/ReactToastify.css"; // Import toastify CSS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDroplet, faCalendarAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const WaterBillPage = () => {
  const [waterBill, setWaterBill] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = JSON.parse(userDataString);
  const userId = userData.unique;

  useEffect(() => {
    const fetchWaterBill = async () => {
      setLoading(true); // Set loading to true before fetch
      try {
        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/admin/waterbill/getspecific/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setWaterBill(response.data.response);
        toast.success("Water bill fetched successfully!"); // Success toast
      } catch (error) {
        console.error("Error fetching water bill:", error);
        setWaterBill(null); // Ensure waterBill is null on error
        if (error.response && error.response.data && error.response.data.response) {
            toast.error(error.response.data.response); // Display specific error from backend
        } else {
            toast.error("Failed to fetch water bill. Please try again."); // Generic error
        }
      } finally {
        setLoading(false); // Set loading to false after fetch
      }
    };

    if (token && userId) { // Ensure both token and userId exist
      fetchWaterBill();
    } else {
        setLoading(false); // No token or userId, stop loading
        toast.warn("Please log in to view your water bill.");
    }
  }, [token, userId]); // Dependency array includes token and userId

  return (
    <AuthGuard>
      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-blue-50 to-cyan-100 p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-3xl w-full py-8 text-center animate-fade-in">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faDroplet} className="text-blue-500 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Water Bill
            </h1>
          </div>
          <span className="text-indigo-600 text-xl font-bold flex items-center justify-center mb-10">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-2xl" />
            Today's Date: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>

          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mb-4"></div>
              <p>Loading your water bill...</p>
            </div>
          ) : waterBill ? (
            <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 p-8 space-y-6 text-left">
              <h2 className="text-3xl font-semibold text-gray-800 border-b-2 border-indigo-200 pb-4 mb-4">
                Water Bill Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-600 text-lg font-medium mb-2">
                    <strong>House Number:</strong>
                  </p>
                  <p className="text-gray-800 text-xl font-bold">
                    {waterBill.house_number}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-lg font-medium mb-2">
                    <strong>Previous Units:</strong>
                  </p>
                  <p className="text-gray-800 text-xl font-bold">
                    {waterBill.previousunits} units
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-lg font-medium mb-2">
                    <strong>Current Units:</strong>
                  </p>
                  <p className="text-gray-800 text-xl font-bold">
                    {waterBill.currentunits} units
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-lg font-medium mb-2">
                    <strong>Amount Due:</strong>
                  </p>
                  <p className="text-green-600 text-3xl font-extrabold">
                    KSh {waterBill.amount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-5xl mb-4" />
              <p>No water bill available for your property at this time.</p>
              <p className="mt-2 text-lg">Please check back later.</p>
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default WaterBillPage;